// /login: email + password. The legacy bootstrap-token form is only shown at /login?token.

import { useEffect, useState, type FormEvent } from "react";
import { needsPlan, passwordLogin, tokenLogin } from "../api";
import { Link, Redirect, navigate, safeNext, useLocation } from "../router";
import { refreshMe, useSession } from "../session";
import { store } from "../store";
import { PageSplash } from "./Shell";
import { mustSetUp } from "../api";
import { HxPage, HxWindow, WelcomeArt } from "./hx";
import { PasswordInput } from "../ui/PasswordInput";
import { CodeStep, OAuthButtons } from "./Signup";

function retryText(sec?: number) {
  if (!sec || sec <= 0) return "in a few minutes";
  if (sec < 90) return `in ${Math.ceil(sec)} seconds`;
  return `in ${Math.ceil(sec / 60)} minutes`;
}

export function LoginPage() {
  const { auth, me } = useSession();
  const { search } = useLocation();
  const next = safeNext(search.get("next"), "/dashboard");
  const showToken = search.has("token");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(search.get("error"));
  // An account whose email isn't confirmed yet: the code we just sent, then on.
  const [confirm, setConfirm] = useState<{ email: string; devCode?: string } | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState(false);

  // Once signed in (either form), load /api/me and go on.
  useEffect(() => {
    if (!signedIn) return;
    void refreshMe().then((a) => {
      // Not set up yet (the gate is on for them): Getting ready first.
      const me = store.get().me;
      if (a.kind === "in")
        navigate(needsPlan(me) ? "/plans" : mustSetUp(me) ? "/start" : next, {
          replace: true,
        });
      else
        setError(
          "Signed in, but the session could not be loaded. Check that cookies are enabled.",
        );
    });
  }, [signedIn, next]);

  if (auth.kind === "in" && !signedIn)
    return (
      <Redirect
        to={mustSetUp(me) ? "/start" : search.get("next") ? next : "/dashboard"}
      />
    );
  if (auth.kind === "unknown") return <PageSplash />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    setError(null);
    const r = await passwordLogin(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      setSignedIn(true);
      return;
    }
    if (r.status === 401) setError("Incorrect email or password.");
    else if (r.status === 429)
      setError(
        `Too many sign-in attempts. Try again ${retryText(r.retryAfter)}.`,
      );
    else if (r.status === 403 && r.body?.verify)
      setConfirm({ email: String(r.body.email ?? email.trim()), devCode: r.body.devCode as string | undefined });
    else if (r.status === 403)
      setError(r.error.toLowerCase() === "forbidden" ? "This account is disabled." : r.error);
    else setError(r.error);
  };

  const submitToken = async (e: FormEvent) => {
    e.preventDefault();
    if (!token.trim() || busy) return;
    setBusy(true);
    setTokenError(null);
    const err = await tokenLogin(token.trim());
    setBusy(false);
    if (err) setTokenError(err);
    else setSignedIn(true);
  };

  if (confirm)
    return (
      <HxPage testid="login">
        <div className="hx-column">
          <CodeStep email={confirm.email} devCode={confirm.devCode} onBack={() => setConfirm(null)} onDone={() => setSignedIn(true)} />
        </div>
      </HxPage>
    );

  return (
    <HxPage testid="login">
      <div className="hx-column">
        <div className="hx-column-head hx-rise">
          <WelcomeArt />
          <h1 className="hx-h1">
            Welcome <em>back.</em>
          </h1>
          <p>Your cloud machine kept working while you were away.</p>
        </div>
        <div className="hx-rise" style={{ ["--i" as string]: 1 }}>
          <OAuthButtons next={next} />
        </div>
        <div className="hx-rise" style={{ ["--i" as string]: 2 }}>
        <HxWindow>
        <form className="hx-form" onSubmit={submit} noValidate>
          <div className="hx-field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoFocus={!showToken}
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? "login-error" : undefined}
              data-testid="login-email"
            />
          </div>
          <div className="hx-field">
            <div className="hx-label-row">
              <label htmlFor="login-password">Password</label>
              <Link
                to={`/forgot${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`}
                data-testid="login-forgot"
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="login-password"
              name="password"
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? "login-error" : undefined}
              data-testid="login-password"
            />
          </div>
          {error && (
            <div
              className="hx-error"
              id="login-error"
              role="alert"
              data-testid="login-error"
            >
              {error}
            </div>
          )}
          <button
            className="hx-btn hx-btn-primary hx-btn-block"
            type="submit"
            disabled={busy || signedIn}
            data-testid="login-submit"
          >
            {busy || signedIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
        </HxWindow>
        </div>
        <p className="hx-foot hx-rise" style={{ ["--i" as string]: 3 }}>
          No account yet?{" "}
          <Link to="/signup" data-testid="login-signup">
            Create one
          </Link>
        </p>
        {showToken && (
          <details className="hx-card hx-form" open>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Use a bootstrap token
            </summary>
            <form
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
              onSubmit={submitToken}
            >
              <div className="hx-field">
                <label htmlFor="token">Access token</label>
                <input
                  id="token"
                  name="token"
                  type="password"
                  autoComplete="off"
                  autoFocus
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your token"
                  data-testid="token-input"
                />
              </div>
              {tokenError && (
                <div className="hx-error" role="alert">
                  {tokenError}
                </div>
              )}
              <button
                className="hx-btn hx-btn-secondary hx-btn-block"
                type="submit"
                disabled={busy || signedIn || !token.trim()}
                data-testid="token-submit"
              >
                Sign in with token
              </button>
            </form>
          </details>
        )}
      </div>
    </HxPage>
  );
}

